/** IMEI check digit (15-digit IMEI / GSM algorithm). */
export function imeiCheckDigit14(first14: string): number {
  let sum = 0
  for (let i = 0; i < 14; i++) {
    let n = parseInt(first14[i], 10)
    if (Number.isNaN(n)) return -1
    if (i % 2 === 1) {
      n *= 2
      sum += n > 9 ? n - 9 : n
    } else {
      sum += n
    }
  }
  return (10 - (sum % 10)) % 10
}

export function isLikelyImei15(d: string): boolean {
  if (!/^\d{15}$/.test(d)) return false
  const chk = imeiCheckDigit14(d.slice(0, 14))
  return chk === parseInt(d[14], 10)
}

const OCR_TO_DIGIT: Record<string, string> = {
  O: '0',
  o: '0',
  Q: '0',
  D: '0',
  '°': '0',
  I: '1',
  l: '1',
  L: '1',
  '|': '1',
  '!': '1',
  S: '5',
  s: '5',
  B: '8',
  Z: '2',
  z: '2',
  g: '9',
}

const IMEI_LABEL = /imei[\s:.#-]*([0-9A-Za-z|\s.\-_/]{12,36})/gi
const OCR_WINDOW = /[0-9OIlL|!SBZzgODQ°.\s\-_/]{15,24}/g

/** Normalize OCR quirks before digit extraction. */
export function normalizeOcrTextForImei(text: string): string {
  return text
    .replace(/\u00a0/g, ' ')
    .replace(/\r/g, '\n')
    .replace(/[—–−]/g, '-')
}

function ocrAlnumToDigits(chunk: string): string {
  let out = ''
  for (const ch of chunk) {
    const mapped = OCR_TO_DIGIT[ch] ?? ch
    if (mapped >= '0' && mapped <= '9') out += mapped
  }
  return out
}

function collectDigitStreams(text: string): string[] {
  const streams: string[] = []
  const plain = text.replace(/\D/g, '')
  if (plain) streams.push(plain)

  const mapped = ocrAlnumToDigits(text)
  if (mapped && mapped !== plain) streams.push(mapped)

  for (const match of text.matchAll(IMEI_LABEL)) {
    const seg = ocrAlnumToDigits(match[1] ?? '')
    if (seg.length >= 14) streams.push(seg)
  }

  for (const line of text.split('\n')) {
    if (/imei/i.test(line)) {
      const seg = ocrAlnumToDigits(line)
      if (seg.length >= 14) streams.push(seg)
    }
  }

  for (const match of text.matchAll(OCR_WINDOW)) {
    const seg = ocrAlnumToDigits(match[0])
    if (seg.length >= 14) streams.push(seg)
  }

  return [...new Set(streams)]
}

function pickBest(valid: string[]): string {
  if (valid.length === 1) return valid[0]
  const pref35 = valid.filter((c) => c.startsWith('35'))
  return pref35[0] ?? valid[0]
}

/** Strip everything except digits (spaces, dashes, dots, etc.). */
export function normalizeImeiDigits(text: string): string {
  return text.replace(/\D/g, '')
}

/** Optional `IMEI:` / `IMEI` prefix from labels or pasted text. */
export function stripImeiLabelPrefix(text: string): string {
  return text.trim().replace(/^imei\s*[:#]?\s*/i, '')
}

/** True when input is only digits and common IMEI grouping separators. */
export function isImeiInputAllowedChars(text: string): boolean {
  const stripped = stripImeiLabelPrefix(text)
  if (!stripped) return false
  return /^[\d\s.\-_/]+$/i.test(stripped)
}

/** Resolve exactly 15 digits from user input (handles spaces and noisy paste). */
export function imeiDigitsFromUserInput(text: string): string | null {
  const stripped = stripImeiLabelPrefix(text)
  const digits = normalizeImeiDigits(stripped)
  if (digits.length === 15) return digits
  if (digits.length > 15) return extractImeiDigits(stripped)
  return digits.length > 0 ? digits : null
}

/** IMEI field typing: allow grouping chars, cap at 15 digits. */
export function sanitizeImeiFieldInput(raw: string): string {
  let digitCount = 0
  let out = ''
  for (const ch of raw) {
    if (/\d/.test(ch)) {
      if (digitCount >= 15) continue
      digitCount += 1
      out += ch
    } else if (/[\s.\-_/]/.test(ch) && digitCount > 0 && digitCount < 15) {
      out += ch
    }
  }
  return out
}

/** Pull a 15-digit IMEI from OCR / pasted noise. Prefer Luhn-valid windows near IMEI labels. */
export function extractImeiDigits(text: string): string | null {
  if (!text?.trim()) return null

  const norm = normalizeOcrTextForImei(text)
  let streams = collectDigitStreams(norm)
  if (streams.length === 0) streams = collectDigitStreams(text)

  const candidates: string[] = []
  for (const stream of streams) {
    if (stream.length < 15) continue
    for (let i = 0; i <= stream.length - 15; i++) {
      candidates.push(stream.slice(i, i + 15))
    }
  }

  const valid = [...new Set(candidates.filter(isLikelyImei15))]
  if (valid.length > 0) return pickBest(valid)

  const longest = streams.reduce((a, b) => (b.length > a.length ? b : a), '')
  if (longest.length >= 15) {
    const fallback = longest.slice(0, 15)
    if (isLikelyImei15(fallback)) return fallback
  }

  return null
}
